import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { SectionEyebrow } from "./SectionEyebrow";
import { TREATMENTS } from "../config/treatments";
import { buildLocalizedPath } from "../lib/langRoutes";

const TreatmentCard = ({ tr, t, i, lang }) => {
  const { ref, inView } = useScrollReveal({ threshold: 0.1 });
  const item = t(`treatments.items.${tr.key}`, { returnObjects: true });
  const to = buildLocalizedPath("treatmentDirect", lang, tr.slug);
  return (
    <article
      ref={ref}
      data-testid={`treatment-card-${tr.key}`}
      style={{ animationDelay: `${i * 90}ms` }}
      className={`reveal ${inView ? "in-view" : ""} group relative bg-[#F6F5F2] border border-[#E4E2DA] hover:border-[#A7CA99] transition-colors overflow-hidden flex flex-col`}
    >
      <Link to={to} className="block relative aspect-[4/3] overflow-hidden bg-[#EFEDE6]">
        <img
          src={tr.hero}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
        />
        <div className="absolute top-4 right-4 px-3 py-1 bg-[#F6F5F2]/95 backdrop-blur-sm">
          <span className="overline text-[#3E5239] text-[10px]">{item.price}</span>
        </div>
      </Link>
      <div className="p-6 lg:p-8 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[#A7CA99]" />
          <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#A7CA99]">
            {item.subtitle}
          </span>
        </div>
        <h3
          data-testid={`treatment-title-${tr.key}`}
          lang="de"
          className="font-serif text-xl lg:text-2xl xl:text-xl font-light text-[#3E5239] leading-tight break-words hyphens-auto"
        >
          {item.title}
        </h3>
        <p className="mt-4 text-sm font-light text-[#6B7A66] leading-relaxed flex-1">
          {item.short_desc}
        </p>
        <Link
          to={to}
          data-testid={`treatment-cta-${tr.key}`}
          className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#3E5239] text-[#F6F5F2] text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-[#A7CA99] hover:text-[#1F2D1D] transition-colors self-start rounded-sm whitespace-nowrap"
        >
          {t("common.learn_more")}
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </Link>
      </div>
    </article>
  );
};

export const Treatments = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "de";

  return (
    <section id="tratamentos" data-testid="treatments-section" className="py-24 lg:py-36 bg-[#EFEDE6] border-y border-[#E4E2DA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <SectionEyebrow num="03">{t("treatments.eyebrow")}</SectionEyebrow>
            <h2 data-testid="treatments-headline" className="font-serif text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.05] text-[#3E5239]">
              {t("treatments.title_l1")}
              <br />
              <span className="italic text-[#A7CA99]">{t("treatments.title_l2")}</span>
            </h2>
          </div>
          <p className="lg:max-w-sm text-[#6B7A66] font-light leading-relaxed">{t("treatments.intro")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-[#E4E2DA]">
          {TREATMENTS.map((tr, i) => (
            <TreatmentCard key={tr.key} tr={tr} t={t} i={i} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
};
